/*
 * @GestionalePD Calendar event dialog UI
 */
import * as React from 'react';
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Grid, Typography, Box, DialogActions, DialogContent, TextField } from '@mui/material';
import { DefaultContainedButton, DefaultOutlinedButton } from '../../Buttons';
import { CustomAutocomplete, CustomTextField } from '../../StyledInputs';
import { LoadingUI } from '../../LayoutStyles';
import API from '../../../api';

const NewLocationDialog = forwardRef(({ openDialog, handleClose, createLocation }, ref) => {
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationStreet, setNewLocationStreet] = useState('');
  const [newLocationZipCode, setNewLocationZipCode] = useState('');
  const [newLocationCountry, setNewLocationCountry] = useState(null);
  const [newLocationRegion, setNewLocationRegion] = useState(null);
  const [newLocationRegionName, setNewLocationRegionName] = useState(null);
  const [newLocationProvince, setNewLocationProvince] = useState(null);
  const [newLocationCity, setNewLocationCity] = useState(null);
  const [newLocationCityName, setNewLocationCityName] = useState(null);

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!newLocationName.trim()) newErrors.newLocationName = true;
    if (!newLocationCountry) newErrors.newLocationCountry = true;

    if (newLocationCountry?.name === italy.name) {
      if (!newLocationRegion) newErrors.newLocationRegion = true;
      if (!newLocationProvince) newErrors.newLocationProvince = true;
      if (!newLocationCity) newErrors.newLocationCity = true;
    } else {
      if (!newLocationRegionName) newErrors.newLocationRegionName = true;
      if (!newLocationCityName) newErrors.newLocationCityName = true;
    }

    if (!newLocationStreet.trim()) newErrors.newLocationStreet = true;
    // if (!newLocationZipCode.trim()) newErrors.newLocationZipCode = true;

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const onCreateLocation = () => {
    if (!validateForm()) return;

    const data = {
      name: newLocationName,
      country_id: newLocationCountry.id,
      region_name: newLocationRegionName,
      province_id: newLocationProvince.id,
      city_id: newLocationCity.id,
      city_name: newLocationCityName,
      street: newLocationStreet,
      zip_code: newLocationZipCode,
    };

    if (newLocationCountry?.name === italy.name) {
      delete data.city_name;
      delete data.region_name;
    } else {
      delete data.city_id;
      delete data.province_id;
    }

    createLocation(data);
  };

  const refreshNewLocationForm = () => {
    setNewLocationName('');
    setNewLocationStreet('');
    setNewLocationZipCode('');
    setNewLocationCountry(null);
    setNewLocationRegion(null);
    setNewLocationRegionName(null);
    setNewLocationProvince(null);
    setNewLocationCity(null);
    setNewLocationCityName(null);
  };

  useImperativeHandle(ref, () => ({
    refreshNewLocationForm,
  }));

  /* ------------------------------ Location Data ----------------------------- */

  const [countriesList, setCountriesList] = useState([]);
  const [regionsList, setRegionsList] = useState([]);
  const [provincesList, setProvincesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [italy, setItaly] = useState({});

  const [locationApiCalled, setLocationApiCalled] = useState(false);

  const getCountriesList = () => {
    API.get('/countries').then((response) => {
      const italyData = response.data.find((element) => element.name === 'Italia');
      setItaly(italyData);
      setCountriesList(response.data);
      setLocationApiCalled(true);
    });
  };

  useEffect(() => {
    if (italy && Object.keys(italy).length > 0) {
      setNewLocationCountry(italy);
    }
  }, [italy]);

  useEffect(() => {
    if (openDialog) {
      getCountriesList();
    }
  }, [openDialog]);

  //   Get Regions List
  useEffect(() => {
    if (newLocationCountry !== null && newLocationCountry !== undefined && newLocationCountry !== '') {
      if (
        typeof newLocationCountry === 'object' &&
        Object.keys(newLocationCountry).length > 0 &&
        newLocationCountry.name === italy.name
      ) {
        API.get(`/countries/regions`, {
          params: {
            id: newLocationCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setRegionsList(response.data);
          } else {
            setRegionsList([]);
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [newLocationCountry]);

  //   Get Provinces List
  useEffect(() => {
    if (newLocationRegion !== null && newLocationRegion !== undefined && newLocationRegion !== '') {
      if (typeof newLocationRegion === 'object' && Object.keys(newLocationRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: newLocationRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setProvincesList(response.data);
          } else {
            setProvincesList([]);
            setCitiesList([]);
          }
        });
      }
    }
  }, [newLocationRegion]);

  //   Get Cities List
  useEffect(() => {
    if (newLocationProvince !== null && newLocationProvince !== undefined && newLocationProvince !== '') {
      if (typeof newLocationProvince === 'object' && Object.keys(newLocationProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: newLocationProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setCitiesList(response.data);
          } else {
            setCitiesList([]);
          }
        });
      }
    }
  }, [newLocationProvince]);

  useEffect(() => {
    if (newLocationCountry && newLocationCountry?.name !== italy.name) {
      setNewLocationProvince('');
      setNewLocationRegion('');
      setNewLocationCity('');
    }

    if (newLocationCountry?.name !== italy.name) {
      setRegionsList([]);
      setProvincesList([]);
      setCitiesList([]);
    }
  }, [newLocationCountry]);

  return (
    <Dialog onClose={handleClose} open={openDialog} fullWidth>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ padding: '12px' }}>
        <Typography variant="h5">Nuova Location</Typography>
      </Box>
      <DialogContent dividers>
        {locationApiCalled ? (
          <Grid container sx={{ padding: '24px' }} spacing={3}>
            <Grid item xs={12}>
              <CustomTextField
                label="Nome"
                required
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.currentTarget.value)}
                error={errors.newLocationName}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12}>
              <CustomAutocomplete
                options={
                  newLocationCountry?.name === italy.name
                    ? countriesList.filter(
                        (country) =>
                          (!newLocationRegion || country.id === newLocationRegion.country_id) &&
                          (!newLocationProvince || country.id === newLocationProvince.country_id)
                      )
                    : countriesList
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    {option.name}
                  </Box>
                )}
                value={newLocationCountry}
                onChange={(e, value) => setNewLocationCountry(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Paese" variant="outlined" required error={errors.newLocationCountry} />
                )}
              />
            </Grid>

            {/* Region */}
            <Grid item xs={12}>
              <CustomAutocomplete
                freeSolo={newLocationCountry?.name !== italy.name}
                options={regionsList.filter(
                  (region) =>
                    (!newLocationProvince || region.region_name === newLocationProvince.region_name) &&
                    (!newLocationCountry || region.country_id === newLocationCountry.id)
                )}
                isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  return option.region_name;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.region_name}>
                    {option.region_name}
                  </Box>
                )}
                value={
                  newLocationRegionName === undefined ||
                  newLocationRegionName === null ||
                  newLocationCountry?.name === italy.name
                    ? newLocationRegion
                    : newLocationRegionName
                }
                onChange={(e, value) => {
                  if (typeof value === 'string') {
                    setNewLocationRegion(value);
                  } else if (value && value.inputValue) {
                    setNewLocationRegion(value.inputValue);
                  } else {
                    setNewLocationRegion(value);
                  }
                }}
                onInputChange={(e, value) => {
                  if (newLocationCountry?.name !== italy.name) {
                    setNewLocationRegionName(value);
                  } else {
                    setNewLocationRegionName(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Regione"
                    variant="outlined"
                    required
                    error={
                      newLocationCountry?.name === italy.name ? errors.newLocationRegion : errors.newLocationRegionName
                    }
                  />
                )}
              />
            </Grid>

            {/* Province */}
            <Grid item xs={12}>
              <CustomAutocomplete
                freeSolo={newLocationCountry?.name !== italy.name}
                disabled={newLocationCountry && newLocationCountry?.name !== italy.name}
                options={provincesList.filter(
                  (province) =>
                    (!newLocationRegion || province.region_name === newLocationRegion.region_name) &&
                    (!newLocationCity || province.id === newLocationCity.province_id) &&
                    (!newLocationCountry || province.country_id === newLocationCountry.id)
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  return option.name;
                }}
                value={newLocationProvince}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    {option.name}
                  </Box>
                )}
                onChange={(e, value) => {
                  if (typeof value === 'string') {
                    setNewLocationProvince(value);
                  } else if (value && value.inputValue) {
                    setNewLocationProvince(value.inputValue);
                  } else {
                    setNewLocationProvince(value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Provincia"
                    variant="outlined"
                    required
                    error={errors.newLocationProvince}
                  />
                )}
              />
            </Grid>

            {/* City */}
            <Grid item xs={12}>
              <CustomAutocomplete
                freeSolo={newLocationCountry?.name !== italy.name}
                options={citiesList.filter(
                  (city) => !newLocationProvince || city.province_id === newLocationProvince.id
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  return option.name;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    {option.name}
                  </Box>
                )}
                value={
                  newLocationCityName === undefined ||
                  newLocationCityName === null ||
                  newLocationCountry?.name === italy.name
                    ? newLocationCity
                    : newLocationCityName
                }
                onChange={(e, value) => {
                  if (typeof value === 'string') {
                    setNewLocationCity(value);
                  } else if (value && value.inputValue) {
                    setNewLocationCity(value.inputValue);
                  } else {
                    setNewLocationCity(value);
                  }
                }}
                onInputChange={(e, value) => {
                  if (newLocationCountry?.name !== italy.name) {
                    setNewLocationCityName(value);
                  } else {
                    setNewLocationCityName(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Comune"
                    variant="outlined"
                    required
                    error={
                      newLocationCountry?.name === italy.name ? errors.newLocationCity : errors.newLocationCityName
                    }
                  />
                )}
              />
            </Grid>

            {/* Street */}
            <Grid item xs={12} md={8}>
              <CustomTextField
                label="Indirizzo"
                value={newLocationStreet}
                required
                onChange={(e) => setNewLocationStreet(e.currentTarget.value)}
                error={errors.newLocationStreet}
              />
            </Grid>

            {/* Zip Code */}
            <Grid item xs={12} md={4}>
              <CustomTextField
                label="Codice Postale"
                value={newLocationZipCode}
                // required
                onChange={(e) => setNewLocationZipCode(e.currentTarget.value)}
                error={errors.newLocationZipCode}
              />
            </Grid>
          </Grid>
        ) : (
          LoadingUI()
        )}
      </DialogContent>

      <DialogActions>
        <DefaultOutlinedButton onClick={handleClose}>Chiudi</DefaultOutlinedButton>
        {locationApiCalled && <DefaultContainedButton onClick={onCreateLocation}>Salva</DefaultContainedButton>}
      </DialogActions>
    </Dialog>
  );
});

export default NewLocationDialog;

// Calendar Dialog parameter type declaration
NewLocationDialog.propTypes = {
  openDialog: PropTypes.bool,
  handleClose: PropTypes.func,
};
